import { Bell, BellOff, X } from 'lucide-react';
import type { NotificationItem } from '../../hooks/useNotifications';
import { formatRelativeTime } from '../../lib/relative-time';
import { cn } from '../../lib/utils';

/**
 * The side panel is ~340px wide and the portal pages are not, so the same list
 * has to render at two sizes. A density prop rather than two components: the
 * markup and behaviour are identical, only the type scale differs.
 */
export type NotificationDensity = 'compact' | 'comfortable';

export interface NotificationListProps {
  notifications: NotificationItem[];
  density?: NotificationDensity;
  onOpen: (notification: NotificationItem) => void;
  onDismiss: (notification: NotificationItem) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  className?: string;
}

export function NotificationList({
  notifications,
  density = 'comfortable',
  onOpen,
  onDismiss,
  emptyTitle = 'No notifications',
  emptyDescription = "You're all caught up.",
  className,
}: NotificationListProps) {
  const compact = density === 'compact';

  if (notifications.length === 0) {
    return (
      <div className={cn('p-8 text-center text-muted-foreground', className)}>
        <BellOff className="mx-auto mb-2 h-8 w-8 opacity-20" />
        <p className={compact ? 'text-xs' : 'text-sm'}>{emptyTitle}</p>
        <p className={cn('mt-1', compact ? 'text-[10px]' : 'text-xs')}>{emptyDescription}</p>
      </div>
    );
  }

  return (
    <ul className={cn('divide-y divide-border', className)}>
      {notifications.map((notification) => (
        <li
          key={notification.id}
          className={cn(
            'group flex gap-2 transition-colors hover:bg-muted/30',
            compact ? 'p-3' : 'p-4',
            // The unread marker is a border rather than a background so it stays
            // legible against the hover state.
            !notification.isRead && 'border-l-2 border-l-primary'
          )}
        >
          <button
            type="button"
            className="flex min-w-0 flex-1 gap-2 text-left"
            onClick={() => onOpen(notification)}
          >
            <Bell
              className={cn('mt-0.5 shrink-0 text-primary', compact ? 'h-3.5 w-3.5' : 'h-4 w-4')}
            />
            <span className="min-w-0">
              <span
                className={cn(
                  'block',
                  compact ? 'text-xs' : 'text-sm',
                  notification.isRead ? 'font-normal' : 'font-semibold'
                )}
              >
                {notification.label}
              </span>
              <span
                className={cn(
                  'mt-0.5 line-clamp-2 block text-muted-foreground',
                  compact ? 'text-[10px]' : 'text-xs'
                )}
              >
                {notification.content}
              </span>
              <span
                className={cn(
                  'mt-1 block text-muted-foreground/80',
                  compact ? 'text-[10px]' : 'text-[11px]'
                )}
              >
                {formatRelativeTime(notification.createdAt * 1000)}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => onDismiss(notification)}
            className="self-start rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
            title="Dismiss notification"
          >
            <X className="h-3 w-3" />
          </button>
        </li>
      ))}
    </ul>
  );
}
